import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body2,
    FilledCard,
    RichText,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { isGovernmentOfferType } from '@phx/data-sources/medication-marketplace';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import { useMedicationDisclaimers } from '../../../../hooks/use-medication-disclosures';
import { usePharmacyName } from '../../../../hooks/use-pharmacy-name';
import { filterOffersBySubPathway } from '../../../../hooks/use-prescription-offers';
import type { PricingCardProps } from '../common/shared';
import { usePriceQualifier } from '../common/use-price-qualifier';

import { useGovernmentPricing } from './use-government-pricing';

export const GovernmentPricing: FC<PricingCardProps> = ({ offer }) => {
    const { t } = useTranslation('lilly');
    const { allOffers, medication } = useFillPrescription();
    const { subPathway } = useFillPathway();

    const governmentOffers = allOffers.filter((offer) =>
        isGovernmentOfferType(offer.headlinePriceType)
    );
    const subPathwayOffers = filterOffersBySubPathway(
        governmentOffers,
        subPathway
    );

    const hasPricing = subPathwayOffers.some(
        (offer) => offer.headlinePriceType === 'MedicareBridge'
    );

    const { patientPay, priceType } = useGovernmentPricing(offer);
    const priceQualifier = usePriceQualifier({
        medication,
        patientPay,
        priceType,
    });

    const isBridgePrice = priceType === 'MedicareBridge';

    const richDisclaimers = useMedicationDisclaimers({
        headlinePriceType: priceType,
        medication,
        slot: 'price-card',
    });

    const pharmacyName = usePharmacyName(
        offer?.provider ?? subPathwayOffers[0]?.provider
    );

    const disclaimer =
        pharmacyName && (!!offer || subPathwayOffers.length === 1)
            ? t('fill.pricing.feesMayApplyWithPharmacy', {
                  pharmacyName,
                  termsDisclaimer: '',
              })
            : t('fill.pricing.feesMayApply', { termsDisclaimer: '' });

    const showRichDisclaimer = !!richDisclaimers.length;

    return (
        <>
            <FilledCard
                withBorder
                color="primary"
                data-testid="pharmacy-pricing-card"
            >
                <Stack>
                    {isBridgePrice ||
                    !hasPricing ||
                    offer?.headlinePriceType === 'GovernmentPriceAtPharmacy' ? (
                        <Subtitle2>
                            {t('fill.pricing.finalPriceAtPharmacy')}
                        </Subtitle2>
                    ) : (
                        <>
                            <Subtitle1>
                                {t('fill.pricing.asLowAs', {
                                    price: patientPay,
                                    qualifier: priceQualifier?.text ?? '',
                                })}
                            </Subtitle1>
                        </>
                    )}
                    {priceQualifier?.description ? (
                        <RichText {...priceQualifier.description} />
                    ) : null}
                </Stack>
            </FilledCard>
            {showRichDisclaimer || hasPricing ? (
                <Stack data-testid="pharmacy-pricing-terms-and-conditions">
                    {!showRichDisclaimer && hasPricing ? (
                        <Body2>{disclaimer}</Body2>
                    ) : null}
                    {showRichDisclaimer
                        ? richDisclaimers.map((richDisclaimer) => {
                              const richContent = richDisclaimer.content;

                              return richContent.map(({ key, ...richLine }) => (
                                  <RichText
                                      key={key}
                                      {...richLine}
                                      values={{ pharmacyName }}
                                  />
                              ));
                          })
                        : null}
                </Stack>
            ) : null}
        </>
    );
};
